import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./ParticipantsList.scss";

import allIcon from "./img/all.png";
import requestIcon from "./img/request.png";
import blockIcon from "./img/block.png";
import unblockIcon from "./img/unblock.png";
import { getAccessToken } from "../../awsAuth";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.amazonaws.com/DefaultAvatar/avatar.png";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const ParticipantsList = ({
  members,
  onClose,
  isAdmin,
  communityId,
  currentUserId,
  onRequestsHandled,
  onMembersUpdate,
}) => {
  const navigate = useNavigate();

  const [localMembers, setLocalMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("members");

  const [requests, setRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqError, setReqError] = useState("");
  const [requestsCount, setRequestsCount] = useState(0);
  const [reqActionId, setReqActionId] = useState(null);

  const [actions, setActions] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [blockedSet, setBlockedSet] = useState(() => new Set());
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    setLocalMembers(members || []);
  }, [members]);

  useEffect(() => {
    if (!communityId) return;

    try {
      const key = "communities:membersOverrides";
      const raw = localStorage.getItem(key);
      const map = raw ? JSON.parse(raw) || {} : {};
      map[String(communityId)] = localMembers.length;
      localStorage.setItem(key, JSON.stringify(map));
    } catch (e) {
      console.debug("membersOverrides localStorage error", e);
    }

    try {
      window.dispatchEvent(
        new CustomEvent("cv:communityMembersChanged", {
          detail: {
            communityId: String(communityId),
            totalMembers: localMembers.length,
          },
        })
      );
    } catch (e) {
      console.debug("communityMembersChanged event failed", e);
    }
  }, [communityId, localMembers.length]);

  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevBodyOverflow || "";
      document.documentElement.style.overflow = prevHtmlOverflow || "";
    };
  }, []);

  const handleRequestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setOpenMenuId(null);
    setMenuPos(null);
    setTimeout(() => onClose?.(), 240);
  }, [isClosing, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (openMenuId) {
          setOpenMenuId(null);
          setMenuPos(null);
          return;
        }
        handleRequestClose();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleRequestClose, openMenuId]);

  useEffect(() => {
    if (!openMenuId) return;

    const closeMenu = () => {
      setOpenMenuId(null);
      setMenuPos(null);
    };

    const onDocPointerDown = (e) => {
      const target = e.target;
      const insideMenu = target?.closest?.(".participants-floating-menu");
      const insideTrigger = target?.closest?.(".more-btn");
      if (insideMenu || insideTrigger) return;
      closeMenu();
    };

    const onViewportChange = () => {
      closeMenu();
    };

    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("touchstart", onDocPointerDown, { passive: true });
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("touchstart", onDocPointerDown);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [openMenuId]);

  useEffect(() => {
    const syncFollowing = async () => {
      if (!currentUserId) return;

      try {
        const res = await fetch(
          `${API_BASE}/api/follow/following/${currentUserId}`,
          { cache: "no-store" }
        );

        if (!res.ok) return;

        const json = await res.json();

        const followingIds = Array.isArray(json.followingIds)
          ? json.followingIds
          : [];

        const map = {};
        for (const uid of followingIds) map[uid] = "followed";

        setActions((prev) => ({ ...map, ...prev }));
      } catch {}
    };

    syncFollowing();
  }, [currentUserId]);

  // заявки в спільноту
  useEffect(() => {
    const load = async () => {
      if (!isAdmin || !communityId) return;

      setReqLoading(true);
      setReqError("");

      try {
        const token = await getAccessToken();

        if (!token) {
          setReqError("Потрібна авторизація");
          return;
        }

        const listRes = await fetch(
          `${API_BASE}/api/communities/${communityId}/requests?adminId=${encodeURIComponent(
            currentUserId || ""
          )}`,
          {
            cache: "no-store",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!listRes.ok) throw new Error("HTTP " + listRes.status);

        const list = await listRes.json();
        setRequests(Array.isArray(list) ? list : []);

        const cntRes = await fetch(
          `${API_BASE}/api/communities/${communityId}/requests/count`,
          {
            cache: "no-store",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const cntJson = await cntRes.json().catch(() => ({ count: 0 }));
        setRequestsCount(Number(cntJson.count || 0));
      } catch (e) {
        console.error("requests load error:", e);
        setReqError("Не вдалося отримати заявки");
      } finally {
        setReqLoading(false);
      }
    };

    load();
  }, [isAdmin, communityId, currentUserId, onRequestsHandled]);

  // список заблокованих у спільноті
  useEffect(() => {
    const loadBlocks = async () => {
      try {
        if (!isAdmin || !communityId) return;

        const token = await getAccessToken();
        if (!token) return;

        const res = await fetch(
          `${API_BASE}/api/block/community/${communityId}/blocks`,
          {
            cache: "no-store",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error("Не вдалося отримати блоки спільноти:", err);
          return;
        }

        const items = await res.json();

        const set = new Set(
          (items || [])
            .filter((it) => it?.SK?.startsWith?.("BLOCK#"))
            .map((it) => String(it.SK).replace("BLOCK#", ""))
        );

        setBlockedSet(set);
      } catch (e) {
        console.error("Помилка завантаження блоків спільноти:", e);
      }
    };

    loadBlocks();
  }, [isAdmin, communityId]);

  const isBlocked = useCallback(
    (uid) => blockedSet.has(uid),
    [blockedSet]
  );

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();

    return q
      ? localMembers.filter((m) =>
          (m.username || "").toLowerCase().includes(q)
        )
      : localMembers;
  }, [search, localMembers]);

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();

    return q
      ? requests.filter((r) =>
          (r.username || "").toLowerCase().includes(q)
        )
      : requests;
  }, [search, requests]);

  const handleMemberClick = (id) => {
    onClose?.();
    navigate(`/profile/${id}`);
  };

  const handleFollowToggle = async (memberId) => {
    const isFollowing = actions[memberId] === "followed";

    try {
      const token = await getAccessToken().catch(() => null);
      await fetch(`${API_BASE}/api/follow/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          followerId: currentUserId,
          followedId: memberId,
        }),
      });

      setActions((p) => ({
        ...p,
        [memberId]: isFollowing ? null : "followed",
      }));
    } catch {}
  };

  const approveRequest = useCallback(
    async (userId) => {
      if (!communityId || !currentUserId || reqActionId) return;

      setReqActionId(userId);
      setRequests((prev) => prev.filter((r) => r.userId !== userId));
      setRequestsCount((c) => Math.max(0, c - 1));
      onRequestsHandled?.(-1);

      try {
        const token = await getAccessToken();

        if (!token) {
          throw new Error("No token");
        }

        const res = await fetch(
          `${API_BASE}/api/communities/${communityId}/requests/${userId}/approve`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ adminId: currentUserId }),
          }
        );

        if (!res.ok) throw new Error("HTTP " + res.status);

        const accepted = requests.find((r) => r.userId === userId);

        if (accepted) {
          setLocalMembers((prev) => {
            const next = [
              ...prev,
              {
                userId,
                username: accepted.username,
                avatar: accepted.avatar,
                role: "member",
              },
            ];

            onMembersUpdate?.(next);
            return next;
          });
        }
      } catch (e) {
        console.error("approve request error:", e);

        setRequests((prev) => [
          ...prev,
          ...requests.filter((r) => r.userId === userId),
        ]);

        setRequestsCount((c) => c + 1);
        onRequestsHandled?.(1);
        alert("Не вдалося схвалити заявку.");
      } finally {
        setReqActionId(null);
      }
    },
    [
      communityId,
      currentUserId,
      reqActionId,
      requests,
      onRequestsHandled,
      onMembersUpdate,
    ]
  );

  const declineRequest = useCallback(
    async (userId) => {
      if (!communityId || !currentUserId || reqActionId) return;

      setReqActionId(userId);
      setRequests((prev) => prev.filter((r) => r.userId !== userId));
      setRequestsCount((c) => Math.max(0, c - 1));
      onRequestsHandled?.(-1);

      try {
        const token = await getAccessToken();

        if (!token) {
          throw new Error("No token");
        }

        const res = await fetch(
          `${API_BASE}/api/communities/${communityId}/requests/${userId}/decline`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ adminId: currentUserId }),
          }
        );

        if (!res.ok) throw new Error("HTTP " + res.status);
      } catch (e) {
        console.error("decline request error:", e);

        setRequests((prev) => [
          ...prev,
          ...requests.filter((r) => r.userId === userId),
        ]);

        setRequestsCount((c) => c + 1);
        onRequestsHandled?.(1);
        alert("Не вдалося відхилити заявку.");
      } finally {
        setReqActionId(null);
      }
    },
    [communityId, currentUserId, reqActionId, requests, onRequestsHandled]
  );

  // блокування / розблокування в межах спільноти
  const toggleCommunityBlock = useCallback(
    async (memberId) => {
      if (!communityId || !currentUserId) return;

      const alreadyBlocked = isBlocked(memberId);

      try {
        const token = await getAccessToken();

        if (!token) {
          alert("Потрібна авторизація");
          return;
        }

        if (alreadyBlocked) {
          const res = await fetch(
            `${API_BASE}/api/block/community/${communityId}/block/${memberId}`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({}),
            }
          );

          const data = await res.json().catch(() => ({}));

          if (!res.ok) {
            console.error("community unblock error:", data);
            throw new Error(data.error || "HTTP " + res.status);
          }

          setBlockedSet((prev) => {
            const next = new Set(prev);
            next.delete(memberId);
            return next;
          });
        } else {
          const res = await fetch(
            `${API_BASE}/api/block/community/${communityId}/block`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                targetUserId: memberId,
              }),
            }
          );

          const data = await res.json().catch(() => ({}));

          if (!res.ok) {
            console.error("community block error:", data);
            throw new Error(data.error || "HTTP " + res.status);
          }

          setBlockedSet((prev) => {
            const next = new Set(prev);
            next.add(memberId);
            return next;
          });
        }

        setOpenMenuId(null);
        setMenuPos(null);
      } catch (e) {
        console.error("❌ community block toggle:", e);

        alert(
          alreadyBlocked
            ? "Не вдалося розблокувати учасника в спільноті."
            : "Не вдалося заблокувати учасника в спільноті."
        );
      }
    },
    [communityId, currentUserId, isBlocked]
  );

  const openFloatingMenu = useCallback(
    (memberId, buttonEl) => {
      if (!buttonEl || typeof window === "undefined") return;

      const rect = buttonEl.getBoundingClientRect();
      const menuWidth = 250;
      const gap = 8;

      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth || 0;

      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;

      let left = rect.right - menuWidth;

      if (left < 12) left = 12;

      if (left + menuWidth > viewportWidth - 12) {
        left = Math.max(12, viewportWidth - menuWidth - 12);
      }

      let top = rect.bottom + gap;

      if (top > viewportHeight - 100) {
        top = Math.max(12, rect.top - gap - 64);
      }

      setOpenMenuId((prev) => (prev === memberId ? null : memberId));

      setMenuPos(() => {
        if (openMenuId === memberId) return null;

        return {
          top,
          left,
          width: menuWidth,
        };
      });
    },
    [openMenuId]
  );

  const activeMember = useMemo(() => {
    if (!openMenuId) return null;

    return localMembers.find((m) => m.userId === openMenuId) || null;
  }, [openMenuId, localMembers]);

  const activeBlocked = activeMember ? isBlocked(activeMember.userId) : false;

  return (
    <div
      className={`participants-overlay ${isClosing ? "is-closing" : ""}`}
      onClick={handleRequestClose}
      role="dialog"
      aria-modal="true"
      aria-label="Учасники спільноти"
    >
      <div
        className="participants-sheet"
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="participants-header">
          <input
            type="text"
            className="search wide"
            placeholder="Пошук учасників..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="tabs">
            <button
              className={`tab-icon ${tab === "members" ? "active" : ""}`}
              onClick={() => setTab("members")}
              type="button"
            >
              <img src={allIcon} alt="Усі" />
            </button>

            {isAdmin && (
              <button
                className={`tab-icon ${tab === "requests" ? "active" : ""}`}
                onClick={() => setTab("requests")}
                type="button"
              >
                <img src={requestIcon} alt="Запити" />

                {requestsCount > 0 && (
                  <span className="badge">+{requestsCount}</span>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="participants-meta">
          <span className="participants-meta__count">
            Учасники: {localMembers.length}
          </span>
        </div>

        <div className="participants-list">
          {tab === "members" ? (
            filteredMembers.length ? (
              filteredMembers.map((m) => {
                const blocked = isBlocked(m.userId);

                return (
                  <div key={m.userId} className="participant-item">
                    <div
                      className="participant-info"
                      onClick={() => handleMemberClick(m.userId)}
                    >
                      <img
                        src={m.avatar || DEFAULT_AVATAR}
                        alt={m.username}
                        className="participant-avatar"
                      />

                      <span
                        className={`participant-name ${
                          blocked ? "is-blocked" : ""
                        }`}
                      >
                        {m.username}

                        {blocked && (
                          <span className="blocked-label">
                            {" "}
                            · заблокований
                          </span>
                        )}
                      </span>
                    </div>

                    <div className="participant-right">
                      {m.userId !== currentUserId && (
                        <button
                          className={`follow-btn ${
                            actions[m.userId] === "followed"
                              ? "following"
                              : ""
                          }`}
                          onClick={() => handleFollowToggle(m.userId)}
                          type="button"
                        >
                          {actions[m.userId] === "followed"
                            ? "Відстежується"
                            : "Стежити"}
                        </button>
                      )}

                      {isAdmin && m.userId !== currentUserId && (
                        <div className="row-actions">
                          <button
                            type="button"
                            className="more-btn"
                            aria-label="Дії з учасником"
                            onClick={(e) => {
                              e.stopPropagation();
                              openFloatingMenu(m.userId, e.currentTarget);
                            }}
                          >
                            …
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="no-members">Немає учасників</p>
            )
          ) : reqLoading ? (
            <p className="hint">Завантаження…</p>
          ) : reqError ? (
            <p className="error">{reqError}</p>
          ) : filteredRequests.length ? (
            filteredRequests.map((r) => (
              <div key={r.userId} className="participant-item">
                <div
                  className="participant-info"
                  onClick={() => handleMemberClick(r.userId)}
                >
                  <img
                    src={r.avatar || DEFAULT_AVATAR}
                    alt={r.username}
                    className="participant-avatar"
                  />

                  <span className="participant-name">{r.username}</span>
                </div>

                <div className="requests-actions">
                  <button
                    className="approve"
                    onClick={() => approveRequest(r.userId)}
                    disabled={reqActionId === r.userId}
                    aria-busy={reqActionId === r.userId ? "true" : "false"}
                    type="button"
                  >
                    {reqActionId === r.userId ? "..." : "Прийняти"}
                  </button>

                  <button
                    className="decline"
                    onClick={() => declineRequest(r.userId)}
                    disabled={reqActionId === r.userId}
                    aria-busy={reqActionId === r.userId ? "true" : "false"}
                    type="button"
                  >
                    {reqActionId === r.userId ? "..." : "Відхилити"}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="no-members">Немає запитів</p>
          )}
        </div>
      </div>

      {openMenuId &&
        activeMember &&
        menuPos &&
        createPortal(
          <div
            className="participants-floating-menu-backdrop"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div
              className="participants-floating-menu"
              style={{
                top: `${menuPos.top}px`,
                left: `${menuPos.left}px`,
                width: `${menuPos.width}px`,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={`menu-item ${activeBlocked ? "unblock" : "danger"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCommunityBlock(activeMember.userId);
                }}
              >
                <span className="menu-icon">
                  <img src={activeBlocked ? unblockIcon : blockIcon} alt="" />
                </span>

                {activeBlocked
                  ? "Розблокувати учасника"
                  : "Заблокувати в спільноті"}
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ParticipantsList;